import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { MATERIAL_ICON_CLOSE, MATERIAL_ICON_VIDEO } from '../../../shared/common/Button/Icon';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import VideoPlayerPopup from '../../../shared/components/VideoPlayerPopup';
import DocumentTitle from '../../../shared/components/DocumentTitle';
import { removeLocalContentCopies } from '../../../store/libx/actions';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { AppDispatch } from '../../../store';
import {
    invalidateLocalContentCollectionProducts,
    useLocalContentCollectionProducts
} from '../../../api/local-content/collection/useLocalContentCollectionProducts';
import queryString from 'query-string';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import { useHistory, useLocation } from 'react-router';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { AppState } from '../../../store/appState';
import { FilterBar } from '../../../shared/common/FilterBar';
import { LOCAL_COLLECTION_SORT_BY } from '../../../shared/common/SortByDropDown';
import NoElementsFound, {
    NO_ELEMENTS_LC_COLLECTION_TITLES,
    NO_ELEMENTS_LC_TITLES_FILTERED
} from '../../../shared/components/NoElementsFound';
import ProductList from '../../../shared/common/ProductList';
import CollectionProductElement from './CollectionProductElement';
import Pagination, { getPaginationStateObject } from '../../../shared/common/Pagination';
import FilterUtils from '../../../shared/utils/utils-filter';
import { DISPLAY_TYPE_TILE } from '../../../store/displayType/types';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchLocalContentCollectionAggregationsQuickSearch } from '../../../api/local-content/collection/fetchLocalContentCollectionAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchLocalContentCollectionAggregations } from '../../../api/local-content/collection/fetchLocalContentCollectionAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CopyDetailsPopup } from '../../collection/copyDetailsPopup/CopyDetailsPopup';
import { Article } from '../../../../models/Article';
import { ButtonContainer } from './ManageModal.styled';
import {
    invalidateHasRemovableCopy,
    useHasRemovableCopy
} from '../../../api/local-content/collection/useHasRemovableCopy';
import { invalidateProductCopies } from '../../../shop/cart/api/useProductCopies';
import { useAddProductToGroup } from '../../../api/productGroups/useAddProductToGroup';
import Utils from '../../../shared/utils';

const StButtonLine = styled.div.attrs({ 'data-displayname': 'HeaderButtonLine' })`
    margin-bottom: 62px;
`;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 54;

const FilterBarWithSpace = styled(FilterBar)`
    padding: 0 0 5px 0;
`;

export const LocalContentCollectionPage = () => {
    const { t } = useTranslation();
    const [manageProduct, setManageProduct] = useState<(ProductMetaData & Article) | null>(null);
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const { search } = useLocation();
    const history = useHistory();
    const authentication = useSelector((state: AppState) => state.authentication);
    const isInPartnerContext = Utils.isInPartnerContext(authentication);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.TITLE_AZ;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { products, isLoading: isProductsLoading } = useLocalContentCollectionProducts({
        filterMap: filter,
        pagination: {
            page,
            pageSize,
            sortBy
        }
    });

    //region FILTERBAR_V2
    const { filters } = useProductFilters();
    const isFiltered = FilterUtils.hasActiveFilter(filters);
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'localContentCollectionPage',
            'aggregation',
            filters,
            expandedFilterGroups
        ],
        []
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchLocalContentCollectionAggregations(options),
        []
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options => fetchLocalContentCollectionAggregationsQuickSearch(options),
        []
    );

    //endregion

    const onRemoveCopies = useCallback(() => {
        if (!manageProduct?.id) {
            return;
        }

        dispatch(removeLocalContentCopies(manageProduct.id)).then(() => {
            setManageProduct(null);
            invalidateLocalContentCollectionProducts().then();
            invalidateHasRemovableCopy(manageProduct.id).then();
            invalidateProductCopies(manageProduct.id).then();
        });
    }, [dispatch, manageProduct?.id]);

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = (nextPage: number) => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const addProductToGroup = useAddProductToGroup();

    const { data: hasRemovableCopy } = useHasRemovableCopy(manageProduct?.id ?? null);

    const loading = isProductsLoading;

    return (
        <DocumentTitle title={t('components.documentTitle.localContentCollection')}>
            <LoadingWrapper>
                <LoadingOverlay loading={loading} />
            </LoadingWrapper>
            {manageProduct ? (
                <CopyDetailsPopup
                    onClosePopup={() => setManageProduct(null)}
                    onAddPrice={async () => {}}
                    product={manageProduct}
                    isBuyingOptionEnabled={false}
                    isLocalContentVariant={true}
                    removeCopyButton={
                        hasRemovableCopy ? (
                            <ButtonContainer>
                                <Button
                                    size={ButtonSize.SMALL}
                                    theme={ButtonTheme.DANGER}
                                    text={t('yourLocalContent.localContentCollection.removeFrom', {
                                        collectionLabelPrefix: isInPartnerContext
                                            ? t('yourLocalContent.localContentCollection.sharedCollection')
                                            : t('yourLocalContent.localContentCollection.individualCollection')
                                    })}
                                    icon={MATERIAL_ICON_CLOSE}
                                    onClick={onRemoveCopies}
                                />
                            </ButtonContainer>
                        ) : null
                    }
                    shopLanguage={undefined}
                />
            ) : null}
            <VideoPlayerPopup
                showVideo={showIntroVideo}
                hideVideo={() => setShowIntroVideo(false)}
                vimeoId={272097594}
            />
            <ContainerInnerWithColumns>
                <StButtonLine>
                    <Button
                        text={t('yourLocalContent.localContentCollection.introductionAndTraining')}
                        icon={MATERIAL_ICON_VIDEO}
                        onClick={() => setShowIntroVideo(true)}
                        size={ButtonSize.LARGE}
                        theme={ButtonTheme.POSITIVE_INVERT}
                    />
                </StButtonLine>
                <>
                    {products.list.length === 0 ? null : (
                        <FilterBarWithSpace
                            dispatch={dispatch}
                            totalProducts={products.count}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            sorting={LOCAL_COLLECTION_SORT_BY}
                            hideGroupBy={true}
                            hideSelectButtons={true}
                            displayType={display}
                            sortBy={sortBy}
                            onSortChange={setSortBy}
                            currentPage={page}
                            pageSize={pageSize}
                            loading={loading}
                            onDisplayChange={onDisplayChange}
                            displayFilter={displayFilter}
                        />
                    )}
                    <div className="content-area-inner flex-ca">
                        {displayFilter ? (
                            <ResponsiveFilterBar
                                visibleFilterGroups={visibleFilterGroups}
                                buildQueryKey={aggregationsQueryKeyBuilder}
                                fetchAggregations={fetchAggregations}
                                fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                            />
                        ) : null}
                        {products.list.length === 0 && !loading ? (
                            <NoElementsFound
                                type={isFiltered ? NO_ELEMENTS_LC_TITLES_FILTERED : NO_ELEMENTS_LC_COLLECTION_TITLES}
                            />
                        ) : (
                            <div className={classNames({ 'min-h-screen': isProductsLoading }, 'ca-main')}>
                                <div className="ca-main-inner">
                                    <ProductList
                                        displayType={display ?? DISPLAY_TYPE_TILE}
                                        products={products.list}
                                        onAddToGroup={addProductToGroup}
                                        tileItemComponent={CollectionProductElement}
                                        listItemComponent={CollectionProductElement}
                                        onManageButtonClick={(product: ProductMetaData & Article) =>
                                            setManageProduct(product)
                                        }
                                        currentPage={page}
                                    />
                                    <Pagination
                                        totalItems={products.count}
                                        onPageChange={setPage}
                                        currentPage={page}
                                        pageSize={pageSize}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            </ContainerInnerWithColumns>
        </DocumentTitle>
    );
};
